/**
 * ZoneStripViz component.
 *
 * An alternative to the concentric rings visualization.
 * Renders the 4 security tiers as horizontal panels stacked vertically,
 * each showing the zone name, sensor dots, and device count.
 *
 * Uses the same concentric-geometry.ts helpers for color/saturation so all
 * three viz modes (zones, strip, plan) remain visually consistent.
 *
 * Design spec reference: section 3.3 "Zone strip mode"
 * Phase 3 note: alert animations in strip mode are Plan 03-06 scope.
 */
import type { ZoneSnapshot, SensorSnapshot, ZoneId } from './schemas';
import { getRingSaturationColor, getRingOpacity } from './concentric-geometry';
import { tokenValues } from '@/design/tokens';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  /** The 4 security zones from live-state. */
  zones: ZoneSnapshot[];
  /** Sensor nodes for health display. */
  sensors: SensorSnapshot[];
  /** Device counts per ring (all zeros in Phase 3 until Plan 03-06 fills from WS). */
  deviceCountPerRing: Record<ZoneId, number>;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Health indicator dot for a sensor. */
function SensorDotMini({ health }: { health: SensorSnapshot['health'] }) {
  const color =
    health === 'healthy'
      ? tokenValues.colors.status.ok
      : health === 'stale'
        ? tokenValues.colors.status.warn
        : tokenValues.colors.status.alert;

  return (
    <span
      style={{
        display: 'inline-block',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
      title={health}
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * ZoneStripViz: linear panel representation of the four security tiers.
 *
 * @param zones - Zone snapshots ordered from outer to inner
 * @param sensors - Sensor nodes to display health indicators
 * @param deviceCountPerRing - Per-ring device counts driving saturation
 */
export function ZoneStripViz({ zones, sensors, deviceCountPerRing }: Props) {
  // Sort zones outer to inner (tier ascending)
  const sortedZones = [...zones].sort((a, b) => a.tier - b.tier);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '24px',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
      }}
    >
      {sortedZones.map((zone) => {
        const count = deviceCountPerRing[zone.id] ?? 0;
        const strokeColor = getRingSaturationColor(count, tokenValues);
        const opacity = getRingOpacity(count);
        const zoneSensors = sensors.slice(0, 2); // Phase 3: show up to 2 per strip row

        return (
          <div
            key={zone.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '6px',
              borderLeft: `3px solid ${strokeColor}`,
              opacity,
              background: `hsla(220, 20%, 8%, 0.8)`,
              transition: 'opacity 200ms ease, border-color 200ms ease',
            }}
          >
            {/* Zone name */}
            <span
              style={{
                fontFamily: tokenValues.fonts.sans,
                fontSize: '13px',
                color: tokenValues.colors.text.secondary,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                minWidth: '90px',
              }}
            >
              {zone.name}
            </span>

            {/* Device count */}
            <span
              style={{
                fontFamily: tokenValues.fonts.mono,
                fontSize: '12px',
                color: count > 0 ? strokeColor : tokenValues.colors.text.tertiary,
                minWidth: '30px',
              }}
            >
              {count} device{count !== 1 ? 's' : ''}
            </span>

            {/* Sensor health dots */}
            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              {zoneSensors.map((s) => (
                <SensorDotMini key={s.chip_id} health={s.health} />
              ))}
            </div>
          </div>
        );
      })}

      {zones.length === 0 && (
        <p
          style={{
            color: tokenValues.colors.text.tertiary,
            fontFamily: tokenValues.fonts.sans,
            fontSize: '13px',
            textAlign: 'center',
            padding: '32px',
          }}
        >
          Waiting for first heartbeat...
        </p>
      )}
    </div>
  );
}
